import { type IconNames } from '@dixa/lib-shared-habla';
import { type FileUIPart } from 'ai';

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type SidekickAttachmentType = {
  readonly icon: IconNames;
  readonly label: string;
  readonly extensions: readonly string[];
};

const ATTACHMENT_TYPES = {
  'image/jpeg': {
    icon: 'file-image',
    label: 'JPEG image',
    extensions: ['jpg', 'jpeg'],
  },
  'image/png': { icon: 'file-image', label: 'PNG image', extensions: ['png'] },
  'image/gif': { icon: 'file-image', label: 'GIF image', extensions: ['gif'] },
  'image/webp': {
    icon: 'file-image',
    label: 'WebP image',
    extensions: ['webp'],
  },
  'application/pdf': {
    icon: 'file-pdf',
    label: 'PDF document',
    extensions: ['pdf'],
  },
  'text/plain': {
    icon: 'file-text',
    label: 'Text document',
    extensions: ['txt'],
  },
  'text/csv': { icon: 'file-csv', label: 'CSV file', extensions: ['csv'] },
  'text/markdown': {
    icon: 'file-text',
    label: 'Markdown document',
    extensions: ['md', 'markdown'],
  },
  [XLSX_MEDIA_TYPE]: {
    icon: 'file-excel',
    label: 'Excel spreadsheet',
    extensions: ['xlsx'],
  },
} as const satisfies Record<string, SidekickAttachmentType>;

/** A file type the Sidekick accepts as an attachment. */
export type SidekickAttachmentMediaType = keyof typeof ATTACHMENT_TYPES;

const isAttachmentMediaType = (
  mediaType: string,
): mediaType is SidekickAttachmentMediaType =>
  Object.hasOwn(ATTACHMENT_TYPES, mediaType);

const MEDIA_TYPE_BY_EXTENSION: ReadonlyMap<
  string,
  SidekickAttachmentMediaType
> = new Map(
  Object.entries(ATTACHMENT_TYPES).flatMap(([mediaType, type]) =>
    isAttachmentMediaType(mediaType)
      ? type.extensions.map((extension) => [extension, mediaType] as const)
      : [],
  ),
);

/** Size limit per file, so the base64 payload stays under the 5 MB Bedrock accepts. */
export const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

/** Maximum number of files attached to one message. */
export const MAX_ATTACHMENTS = 5;

/** The `accept` value for the file input, by media type and by extension. */
export const ATTACHMENT_ACCEPT = [
  ...Object.keys(ATTACHMENT_TYPES),
  ...[...MEDIA_TYPE_BY_EXTENSION.keys()].map((extension) => `.${extension}`),
].join(',');

/**
 * Resolves the media type of a picked file, falling back to its extension
 * because browsers often report no type for Markdown or CSV files.
 */
export const getAttachmentMediaType = (
  file: File,
): SidekickAttachmentMediaType | undefined => {
  if (isAttachmentMediaType(file.type)) return file.type;

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return MEDIA_TYPE_BY_EXTENSION.get(extension);
};

/** Whether the attachment is shown as an image thumbnail. */
export const isImageAttachment = (attachment: FileUIPart): boolean =>
  attachment.mediaType.startsWith('image/');

/** The Habla icon for an attachment's file type. */
export const getAttachmentIcon = (attachment: FileUIPart): IconNames =>
  isAttachmentMediaType(attachment.mediaType)
    ? ATTACHMENT_TYPES[attachment.mediaType].icon
    : 'file';

/** A short description of an attachment's file type, e.g. "PDF document". */
export const getAttachmentLabel = (attachment: FileUIPart): string =>
  isAttachmentMediaType(attachment.mediaType)
    ? ATTACHMENT_TYPES[attachment.mediaType].label
    : 'File';

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Reads a file into a file part. The data URL carries the resolved media type,
 * since the AI SDK prefers it over the part's own `mediaType`.
 */
export const toAttachment = async (
  file: File,
  mediaType: SidekickAttachmentMediaType,
): Promise<FileUIPart> => ({
  type: 'file',
  mediaType,
  filename: file.name,
  url: `data:${mediaType};base64,${await readAsBase64(file)}`,
});
